import { AUTH_ENDPOINTS } from './constants';
import {
  getToken,
  getRefreshToken,
  isTokenExpiringSoon,
  clearAuthData,
} from './auth';
import type { RespostaApi } from '@/types/api';

const API_URL = process.env.NEXT_PUBLIC_API_URL!;

// Single-flight refresh: apenas uma renovacao por vez
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Verifica se a URL corresponde a um endpoint de autenticacao
 * que nao deve receber o header Authorization automaticamente.
 */
function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/**
 * Constroi um Error a partir de uma response nao-ok, extraindo a
 * mensagem amigavel do RespostaApi quando disponivel.
 */
async function buildError(response: Response): Promise<Error> {
  const text = await response.text().catch(() => '');
  let mensagemAmigavel = '';

  if (text) {
    try {
      const parsed = JSON.parse(text) as Partial<RespostaApi>;
      if (typeof parsed?.mensagem === 'string' && parsed.mensagem.trim()) {
        mensagemAmigavel = parsed.mensagem;
      }
    } catch {
      // Nao era JSON — segue para fallback
    }
  }

  if (!mensagemAmigavel) {
    if (response.status === 0 || response.status >= 502) {
      mensagemAmigavel = 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
    } else if (response.status === 504) {
      mensagemAmigavel = 'O servidor demorou para responder. Tente novamente em instantes.';
    } else if (response.status === 403) {
      mensagemAmigavel = 'Você não tem permissão para realizar esta operação.';
    } else if (response.status === 404) {
      mensagemAmigavel = 'Recurso não encontrado.';
    } else if (response.status >= 500) {
      mensagemAmigavel = 'Ocorreu um erro no servidor. Tente novamente mais tarde.';
    } else {
      mensagemAmigavel = `Erro ${response.status}: ${response.statusText || 'Falha na requisição'}`;
    }
  }

  const err = new Error(mensagemAmigavel) as Error & { status?: number; respostaApi?: RespostaApi };
  err.status = response.status;
  return err;
}

/**
 * Verifica se a URL aponta para o S3 (downloads diretos).
 */
function isS3Url(url: string): boolean {
  return url.startsWith('https://nossosrelatorios.s3');
}

/**
 * Executa a renovacao do token usando o refresh_token.
 * Retorna o novo access_token ou null em caso de falha.
 */
async function doRefreshToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_URL}/api/PortalContador/RenovarToken`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) return null;

    const data: RespostaApi = await response.json();

    if (data.sucesso && data.retorno) {
      const { token, refresh_token, expira_em_minutos } = data.retorno;

      // Atualizar dados no localStorage
      const { setAuthData, getDadosContador } = await import('./auth');
      const dadosContador = getDadosContador();

      if (dadosContador) {
        dadosContador.token = token;
        setAuthData(
          token,
          refresh_token || refreshToken,
          expira_em_minutos || 1440,
          dadosContador
        );
      }

      return token;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Garante que apenas uma renovacao de token ocorre por vez (single-flight).
 */
async function refreshTokenSingleFlight(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = doRefreshToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * Renova o token proativamente se estiver proximo de expirar.
 */
function refreshProactively(): void {
  if (isRefreshing) return;

  const token = getToken();
  if (!token) return;

  if (isTokenExpiringSoon()) {
    refreshTokenSingleFlight().catch(() => {
      // Falha silenciosa na renovacao proativa
    });
  }
}

/**
 * Executa uma requisicao HTTP com tratamento automatico de autenticacao.
 */
async function request<T = any>(
  url: string,
  options: RequestInit = {},
  isRetry = false
): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  const headers = new Headers(options.headers);

  // Nao adicionar Authorization para endpoints de auth ou URLs do S3
  if (!isAuthEndpoint(fullUrl) && !isS3Url(fullUrl)) {
    const token = getToken();

    // Renovacao proativa antes de expirar
    if (token && isTokenExpiringSoon() && !isRefreshing) {
      refreshProactively();
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Content-Type padrao para JSON apenas quando ha body (POST/PUT/PATCH)
  if (
    options.body &&
    !headers.has('Content-Type') &&
    !(options.body instanceof FormData)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(fullUrl, { ...options, headers });

  // Tratar 401: tentar renovar token e refazer a requisicao uma vez
  if (response.status === 401 && !isRetry && !isAuthEndpoint(fullUrl)) {
    const newToken = await refreshTokenSingleFlight();

    if (newToken) {
      return request<T>(url, options, true);
    }

    // Renovacao falhou: limpar dados e redirecionar
    clearAuthData();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw new Error('Sessão expirada');
  }

  if (!response.ok) {
    throw await buildError(response);
  }

  return response.json();
}

/**
 * Executa uma requisicao HTTP que retorna um Blob (para downloads de arquivos).
 */
async function requestBlob(
  url: string,
  options: RequestInit = {},
  isRetry = false
): Promise<Blob> {
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  const headers = new Headers(options.headers);

  if (!isAuthEndpoint(fullUrl) && !isS3Url(fullUrl)) {
    const token = getToken();

    if (token && isTokenExpiringSoon() && !isRefreshing) {
      refreshProactively();
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  if (
    options.body &&
    !headers.has('Content-Type') &&
    !(options.body instanceof FormData)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(fullUrl, { ...options, headers });

  if (response.status === 401 && !isRetry && !isAuthEndpoint(fullUrl)) {
    const newToken = await refreshTokenSingleFlight();

    if (newToken) {
      return requestBlob(url, options, true);
    }

    clearAuthData();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw new Error('Sessão expirada');
  }

  if (!response.ok) {
    throw await buildError(response);
  }

  // Se o servidor retornou JSON de erro com 200 OK (improvável mas possível),
  // o Content-Type permite identificar — caller que pediu Blob deve receber blob de qualquer forma.
  return response.blob();
}

// API publica
export const apiClient = {
  get: <T = any>(url: string, options?: RequestInit) =>
    request<T>(url, { ...options, method: 'GET' }),

  post: <T = any>(url: string, body?: unknown, options?: RequestInit) =>
    request<T>(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  getBlob: (url: string, options?: RequestInit) =>
    requestBlob(url, { ...options, method: 'GET' }),

  postBlob: (url: string, body?: unknown, options?: RequestInit) =>
    requestBlob(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
};
